// Les livrets réglementés, avec leurs règles réelles : le Livret A, le LDDS qu'on peut ouvrir en plus,
// et le LEP, réservé aux revenus modestes.
// - Un plafond de versements (22 950 € pour le Livret A, 12 000 € pour le LDDS) ; les intérêts
//   versés peuvent le dépasser.
// - Des intérêts calculés par quinzaine : un versement rapporte à partir du 1er ou du 16 qui suit,
//   un retrait cesse de rapporter dès le 1er ou le 16 qui précède.
// - Des intérêts versés une fois par an, le 31 décembre, qui rapportent à leur tour l'année suivante.
// - Un même taux, fixé par l'État et révisé en février et en août d'après l'inflation et les taux
//   de la BCE : 1,7 % depuis le 1er août 2026. Avec une inflation de 2 % dans le jeu, l'épargne des
//   livrets perd un peu de pouvoir d'achat. Pas d'impôt, pas de frais, l'argent reste disponible.
import { round2 } from '../engine/finance';
import type { GameState } from '../engine/state';
import { DAYS_PER_YEAR, dateAtDay } from '../engine/time';

export const LIVRET_A = {
  rate: 0.017,
  /** Plafond des versements. */
  ceiling: 22950,
  /** Montant minimum d'un versement ou d'un retrait. */
  minOperation: 10,
};

/** Le livret de développement durable et solidaire : l'argent y finance la transition énergétique et l'économie sociale. */
export const LDDS = {
  rate: 0.017,
  ceiling: 12000,
  /** Montant minimum d'un versement ou d'un retrait, et du premier versement à l'ouverture. */
  minOperation: 15,
};

/**
 * Le livret d'épargne populaire : un meilleur taux, réservé aux revenus modestes (règles de 2026).
 * Il faut un revenu fiscal de référence sous le plafond ; au-dessus deux années de suite, il est fermé.
 */
export const LEP = {
  rate: 0.025,
  ceiling: 10000,
  /** Premier versement à l'ouverture, puis montant minimum d'un versement ou d'un retrait. */
  minOpening: 30,
  minOperation: 10,
  /** Le plafond du revenu fiscal de référence pour une personne seule ; il suit la hausse des prix. */
  incomeCeiling: 23028,
  /** Le revenu fiscal compte les salaires après un abattement de 10 % pour frais professionnels. */
  taxableShare: 0.9,
};

/** La quinzaine du calendrier : du 1er au 15, puis du 16 à la fin du mois. */
export function quinzaineOf(state: GameState, day: number = state.day): number {
  const date = dateAtDay(state.startDate, day);
  return date.getFullYear() * 24 + date.getMonth() * 2 + (date.getDate() >= 16 ? 1 : 0);
}

/** L'épargne des livrets, disponible à tout moment. */
export function regulatedSavings(state: GameState): number {
  return state.livret + state.ldds + state.lep;
}

/** Ce qu'on peut encore verser sur le Livret A avant le plafond. */
export function livretRoom(state: GameState): number {
  return Math.max(0, round2(LIVRET_A.ceiling - state.livret));
}

/** Ce qu'on peut encore verser sur le LDDS, s'il est ouvert. */
export function lddsRoom(state: GameState): number {
  return state.lddsOpen ? Math.max(0, round2(LDDS.ceiling - state.ldds)) : 0;
}

/** Ce qu'on peut encore verser sur le LEP, s'il est ouvert. */
export function lepRoom(state: GameState): number {
  return state.lepOpen ? Math.max(0, round2(LEP.ceiling - state.lep)) : 0;
}

/** Ce qu'on peut encore verser sur tous les livrets ouverts. */
export function savingsRoom(state: GameState): number {
  return livretRoom(state) + lddsRoom(state) + lepRoom(state);
}

/** Le plafond de revenus du LEP, au niveau des prix du moment. */
export function lepIncomeCeiling(state: GameState): number {
  return Math.round(LEP.incomeCeiling * state.priceLevel);
}

/** Le revenu fiscal de référence de l'an dernier, tel que le jeu l'estime. */
export function lepTaxableIncome(state: GameState): number {
  return Math.round(state.lastTaxYearIncome * LEP.taxableShare);
}

/**
 * Le droit d'ouvrir un LEP : avoir sa propre déclaration de revenus (dès le premier emploi ; avant, on est
 * rattaché au foyer fiscal de ses parents) et des revenus de l'an dernier sous le plafond.
 */
export function lepEligible(state: GameState): boolean {
  return state.firstJobDay !== null && lepTaxableIncome(state) <= lepIncomeCeiling(state);
}

/** Verse sur le Livret A dans la limite du plafond ; renvoie ce qui a vraiment été versé. */
export function depositToLivret(state: GameState, amount: number): number {
  const value = round2(Math.min(Math.max(0, amount), livretRoom(state)));
  if (value <= 0) return 0;
  state.livret = round2(state.livret + value);
  return value;
}

/** Verse sur le LDDS dans la limite du plafond ; renvoie ce qui a vraiment été versé. */
export function depositToLdds(state: GameState, amount: number): number {
  const value = round2(Math.min(Math.max(0, amount), lddsRoom(state)));
  if (value <= 0) return 0;
  state.ldds = round2(state.ldds + value);
  return value;
}

/** Verse sur le LEP dans la limite du plafond ; renvoie ce qui a vraiment été versé. */
export function depositToLep(state: GameState, amount: number): number {
  const value = round2(Math.min(Math.max(0, amount), lepRoom(state)));
  if (value <= 0) return 0;
  state.lep = round2(state.lep + value);
  return value;
}

/** Range de l'argent sur les livrets : le LEP d'abord (le mieux payé), puis le Livret A, puis le LDDS. */
export function depositToSavings(state: GameState, amount: number): number {
  const onLep = depositToLep(state, amount);
  const onLivret = depositToLivret(state, amount - onLep);
  return round2(onLep + onLivret + depositToLdds(state, amount - onLep - onLivret));
}

/** Après un retrait : l'argent retiré cesse de rapporter dès le début de la quinzaine en cours. */
export function onSavingsWithdrawal(state: GameState): void {
  const elapsed = Math.max(0, state.day - state.quinzaineStartDay);
  const livretBase = Math.max(0, Math.min(state.livretBase, state.livret));
  const lddsBase = Math.max(0, Math.min(state.lddsBase, state.ldds));
  const lepBase = Math.max(0, Math.min(state.lepBase, state.lep));
  const removedLivret = state.livretBase - livretBase;
  const removedLdds = state.lddsBase - lddsBase;
  const removedLep = state.lepBase - lepBase;
  if (removedLivret > 0) {
    state.accruedInterest = Math.max(0, state.accruedInterest - (removedLivret * LIVRET_A.rate * elapsed) / DAYS_PER_YEAR);
  }
  if (removedLdds > 0) {
    state.lddsAccrued = Math.max(0, state.lddsAccrued - (removedLdds * LDDS.rate * elapsed) / DAYS_PER_YEAR);
  }
  if (removedLep > 0) {
    state.lepAccrued = Math.max(0, state.lepAccrued - (removedLep * LEP.rate * elapsed) / DAYS_PER_YEAR);
  }
  state.livretBase = livretBase;
  state.lddsBase = lddsBase;
  state.lepBase = lepBase;
}

/** Chaque jour : à chaque nouvelle quinzaine, les versements de la précédente commencent à rapporter. */
export function accrueSavingsInterest(state: GameState, days: number): void {
  const quinzaine = quinzaineOf(state);
  if (quinzaine !== state.lastQuinzaine) {
    state.lastQuinzaine = quinzaine;
    state.quinzaineStartDay = state.day;
    state.livretBase = state.livret;
    state.lddsBase = state.ldds;
    state.lepBase = state.lep;
  }
  state.accruedInterest += (state.livretBase * LIVRET_A.rate * days) / DAYS_PER_YEAR;
  state.lddsAccrued += (state.lddsBase * LDDS.rate * days) / DAYS_PER_YEAR;
  state.lepAccrued += (state.lepBase * LEP.rate * days) / DAYS_PER_YEAR;
}

/** Les intérêts de l'année en cours sur les livrets, pas encore versés. */
export function accruedSavingsInterest(state: GameState): number {
  return state.accruedInterest + state.lddsAccrued + state.lepAccrued;
}

/** Le 31 décembre, chaque livret reçoit ses intérêts de l'année ; renvoie leur total. */
export function creditYearlyInterest(state: GameState): number {
  const onLivret = round2(state.accruedInterest);
  const onLdds = round2(state.lddsAccrued);
  const onLep = round2(state.lepAccrued);
  state.accruedInterest = 0;
  state.lddsAccrued = 0;
  state.lepAccrued = 0;
  state.livret = round2(state.livret + onLivret);
  state.ldds = round2(state.ldds + onLdds);
  state.lep = round2(state.lep + onLep);
  state.totalInterest += onLivret + onLdds + onLep;
  // Les intérêts versés rapportent dès la quinzaine qui commence.
  state.livretBase = state.livret;
  state.lddsBase = state.ldds;
  state.lepBase = state.lep;
  return round2(onLivret + onLdds + onLep);
}
